import React from 'react';
import styled from 'styled-components';
import { sectionShell, sectionHeader, sectionTitle, sectionDescription } from '../../styles/sectionStyles';
import { motion } from 'framer-motion';
import {
  FaBookOpen,
  FaCalendarAlt,
  FaCode,
  FaGraduationCap,
  FaLightbulb,
  FaMedal,
  FaStar,
} from 'react-icons/fa';
import { education, certifications, achievements } from '../../data/constants';

const tones = [
  { color: 'var(--accent)', bg: 'var(--orange-bg)', Icon: FaGraduationCap },
  { color: 'var(--green)', bg: 'var(--green-bg)', Icon: FaBookOpen },
  { color: 'var(--purple)', bg: 'var(--purple-bg)', Icon: FaMedal },
  { color: 'var(--blue)', bg: 'var(--blue-bg)', Icon: FaCode },
];

const highlights = [
  { title: 'Software and information systems', desc: 'My degree studies connect programming, databases, and applied information technology.', icon: FaStar, tone: 'orange' },
  { title: 'Programming foundations', desc: 'My IT diploma and Python studies support the practical development work I do today.', icon: FaBookOpen, tone: 'blue' },
  { title: 'Completed academic research', desc: 'I developed and evaluated an eye-gaze communication system for assistive interaction.', icon: FaMedal, tone: 'green' },
  { title: 'Technical community involvement', desc: 'I contributed as IEEE Webmaster and FOSS Technical Team Lead.', icon: FaLightbulb, tone: 'purple' },
];


const Container = styled.section`
  ${sectionShell}
`;

const Wrapper = styled.div`
  width: min(1180px, 100%);
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 34px;
`;

const Header = styled.div`
  ${sectionHeader}
`;

const Eyebrow = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 12px;
  color: var(--accent);
  font-size: 12px;
  font-weight: 650;
  letter-spacing: 0.18em;
  text-transform: uppercase;
  margin-bottom: 12px;

  &::before,
  &::after {
    content: '';
    width: 42px;
    height: 1px;
    background: var(--surface);
  }

  &::after {
    background: var(--surface);
  }
`;

const Title = styled.h2`
  ${sectionTitle}
`;

const Accent = styled.span`
  color: var(--accent);
`;

const Desc = styled.p`
  ${sectionDescription}
`;

const ContentGrid = styled.div`
  width: 100%;
  display: grid;
  grid-template-columns: minmax(0, 1.08fr) minmax(320px, 0.52fr);
  gap: 26px;
  align-items: start;

  @media (max-width: 980px) {
    grid-template-columns: 1fr;
  }
`;

const TimelineArea = styled.div`
  position: relative;
  display: grid;
  grid-template-columns: 42px 1fr;
  gap: 24px;

  &::before {
    content: '';
    position: absolute;
    left: 20px;
    top: 58px;
    bottom: 70px;
    width: 2px;
    background: var(--surface);
    opacity: 0.35;
  }

  @media (max-width: 620px) {
    grid-template-columns: 28px 1fr;
    gap: 14px;

    &::before {
      left: 13px;
    }
  }
`;

const DotColumn = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: space-around;
  padding: 50px 0 58px;
  z-index: 1;
`;

const TimelineDot = styled.div`
  width: 16px;
  height: 16px;
  border-radius: 50%;
  background: ${({ $tone }) => $tone.color};
  box-shadow: 0 0 0 6px ${({ $tone }) => $tone.bg};
`;

const Cards = styled.div`
  display: flex;
  flex-direction: column;
  gap: 18px;
`;

const EducationItem = styled.article`
  display: grid;
  grid-template-columns: 82px 1fr 92px;
  gap: 18px;
  align-items: center;
  min-height: 150px;
  padding: 22px 24px;
  border-radius: 18px;
  background: var(--surface);
  border: 1px solid var(--border);
  box-shadow: var(--shadow-card);

  @media (max-width: 700px) {
    grid-template-columns: 64px 1fr;
  }
`;

const IconBox = styled.div`
  width: 72px;
  height: 72px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border-radius: 16px;
  color: ${({ $tone }) => $tone.color};
  background: ${({ $tone }) => $tone.bg};
  font-size: 34px;

  @media (max-width: 700px) {
    width: 58px;
    height: 58px;
    font-size: 26px;
  }
`;

const EduMain = styled.div`
  min-width: 0;
`;

const EduTitle = styled.h3`
  margin: 0;
  color: var(--text);
  font-size: 18px;
  line-height: 1.25;
  font-weight: 650;
`;

const School = styled.div`
  margin-top: 7px;
  color: ${({ $tone }) => $tone.color};
  font-size: 14px;
  font-weight: 650;
`;

const Meta = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 14px;
  margin-top: 12px;
  color: var(--muted);
  font-size: 13px;
  font-weight: 700;

  span {
    display: inline-flex;
    align-items: center;
    gap: 8px;
  }
`;

const EduDesc = styled.p`
  max-width: 520px;
  margin: 11px 0 0;
  color: var(--muted);
  font-size: 14px;
  line-height: 1.5;
  font-weight: 500;
`;

const StatusCol = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;

  @media (max-width: 700px) {
    grid-column: 2;
    align-items: flex-start;
    flex-direction: row;
  }
`;

const Status = styled.div`
  padding: 6px 12px;
  border-radius: 999px;
  color: ${({ $tone }) => $tone.color};
  background: ${({ $tone }) => $tone.bg};
  border: 1px solid ${({ $tone }) => $tone.color}33;
  font-size: 12px;
  font-weight: 650;
`;

const SideColumn = styled.aside`
  display: flex;
  flex-direction: column;
  gap: 0;
`;

const SideCard = styled.article`
  padding: 26px;
  border-radius: 18px;
  background: var(--surface);
  border: 1px solid var(--border);
  box-shadow: var(--shadow-card);

  & + & {
    margin-top: -1px;
  }
`;

const SideHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
  margin-bottom: 24px;
`;

const SideIcon = styled.div`
  width: 42px;
  height: 42px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border-radius: 10px;
  color: ${({ $tone }) => $tone.color};
  background: ${({ $tone }) => $tone.bg};
  font-size: 18px;
`;

const SideTitle = styled.h3`
  margin: 0;
  color: var(--text);
  font-size: 17px;
  font-weight: 650;
`;

const HighlightList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px;
`;

const HighlightItem = styled.div`
  display: grid;
  grid-template-columns: 52px 1fr;
  gap: 16px;
  align-items: start;
`;

const HighlightIcon = styled.div`
  width: 48px;
  height: 48px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border-radius: 11px;
  color: ${({ $tone }) => $tone.color};
  background: ${({ $tone }) => $tone.bg};
  font-size: 18px;
`;

const HighlightTitle = styled.h4`
  margin: 0;
  color: var(--text);
  font-size: 14px;
  font-weight: 650;
`;

const HighlightDesc = styled.p`
  margin: 7px 0 0;
  color: var(--muted);
  font-size: 13px;
  line-height: 1.45;
  font-weight: 500;
`;

const getStatus = (item) => item.grade;

const Education = () => {
  return (
    <Container id="education">
      <Wrapper>
        <Header as={motion.div} initial={{ opacity: 0, y: 22 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, amount: 0.3 }} transition={{ duration: 0.5, ease: 'easeOut' }}>
          <Eyebrow>My Education</Eyebrow>
          <Title>Education <Accent>&amp; Qualifications</Accent></Title>
          <Desc>My education combines information technology studies, programming foundations, and completed assistive technology research.</Desc>
        </Header>

        <ContentGrid>
          <TimelineArea>
            <DotColumn aria-hidden="true">
              {education.map((item, index) => (
                <TimelineDot key={item.id ?? index} $tone={tones[index % tones.length]} />
              ))}
            </DotColumn>

            <Cards>
              {education.map((item, index) => {
                const tone = tones[index % tones.length];
                const Icon = tone.Icon;
                return (
                  <EducationItem as={motion.article} key={item.id ?? index} initial={{ opacity: 0, y: 24 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, amount: 0.18 }} transition={{ duration: 0.45, delay: index * 0.05, ease: 'easeOut' }}>
                    <IconBox $tone={tone}><Icon /></IconBox>
                    <EduMain>
                      <EduTitle>{item.degree}</EduTitle>
                      <School $tone={tone}>{item.school}</School>
                      <Meta>
                        <span><FaCalendarAlt /> {item.date}</span>
                      </Meta>
                      <EduDesc>{item.desc}</EduDesc>
                      {item.verificationUrl && <a href={item.verificationUrl} target="_blank" rel="noopener noreferrer">Verify qualification</a>}
                    </EduMain>
                    <StatusCol>
                      <Status $tone={tone}>{getStatus(item)}</Status>
                    </StatusCol>
                  </EducationItem>
                );
              })}
            </Cards>
          </TimelineArea>

          <SideColumn>
            <SideCard as={motion.article} initial={{ opacity: 0, x: 24 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true, amount: 0.2 }} transition={{ duration: 0.45, ease: 'easeOut' }}>
              <SideHeader>
                <SideIcon $tone={tones[0]}><FaGraduationCap /></SideIcon>
                <SideTitle>Education Highlights</SideTitle>
              </SideHeader>
              <HighlightList>
                {highlights.map((item) => {
                  const tone = item.tone === 'blue' ? tones[3] : item.tone === 'green' ? tones[1] : item.tone === 'purple' ? tones[2] : tones[0];
                  const Icon = item.icon;
                  return (
                    <HighlightItem key={item.title}>
                      <HighlightIcon $tone={tone}><Icon /></HighlightIcon>
                      <div>
                        <HighlightTitle>{item.title}</HighlightTitle>
                        <HighlightDesc>{item.desc}</HighlightDesc>
                      </div>
                    </HighlightItem>
                  );
                })}
              </HighlightList>
            </SideCard>
          </SideColumn>
        </ContentGrid>
        {certifications.length > 0 && <section aria-label="Professional certifications"><h3>Professional Certifications</h3>{certifications.map(item => <SideCard key={item.id || item.title}><h4>{item.title}</h4>{item.issuer && <p>{item.issuer}</p>}{item.date && <p>{item.date}</p>}{item.verificationUrl && <a href={item.verificationUrl} target="_blank" rel="noopener noreferrer">Verify certificate</a>}</SideCard>)}</section>}
        {achievements.length > 0 && <section aria-label="Academic achievements"><h3>Academic Achievements</h3>{achievements.map(item => <SideCard key={item.id || item.title}><h4>{item.title}</h4>{item.description && <p>{item.description}</p>}</SideCard>)}</section>}
      </Wrapper>
    </Container>
  );
};

export default Education;
